import React from 'react';
import { cn } from '../../lib/utils';
import { FieldLabel } from '../field-label';

/**
 * Server-safe field wrapper. It renders the label, hint and error with the same ids as `Field` (`${id}-hint`, `${id}-error`)
 * but does NOT wire the child control: the caller must give the control `id`, `aria-describedby` (fieldHintId/fieldErrorId),
 * `aria-invalid` and `aria-required` itself. Server pages show errors on load, so the error text has no live region.
 */
export function fieldHintId(id: string): string {
  return `${id}-hint`;
}

export function fieldErrorId(id: string): string {
  return `${id}-error`;
}

export interface FieldStaticProps {
  id: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  className?: string;
  /** Pass id and message references to this control using the exported ID helpers. */
  children: React.ReactNode;
}

/** Server-safe field wrapper for controls whose props are supplied by the caller. */
export function FieldStatic({
  id,
  label,
  hint,
  error,
  required,
  optional,
  className,
  children,
}: FieldStaticProps) {
  const hasHint = Boolean(hint);
  const hasError = Boolean(error);
  return (
    <div
      className={cn('w-full max-w-[30rem] min-w-0 break-words [overflow-wrap:anywhere]', className)}
      data-field
    >
      <FieldLabel htmlFor={id} required={required} optional={optional}>
        {label}
      </FieldLabel>
      {children}
      {hasHint && (
        <p id={fieldHintId(id)} className="mt-1 text-caption text-text-muted">
          {hint}
        </p>
      )}
      {hasError && (
        <p id={fieldErrorId(id)} className="mt-1 text-caption text-status-danger-fg">
          {error}
        </p>
      )}
    </div>
  );
}
